import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Clock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { normalizePhoneNumber } from '@/lib/phone-utils';

interface ConversationWindowStatusProps {
  customerPhone?: string;
  className?: string;
  onStatusChange?: (isOpen: boolean) => void;
}

export function WhatsAppConversationWindowStatus({
  customerPhone,
  className = '',
  onStatusChange
}: ConversationWindowStatusProps) {
  const { toast } = useToast();
  const [windowStatus, setWindowStatus] = useState<{
    isOpen: boolean;
    expiresAt?: string;
    timeRemaining?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (customerPhone) {
      checkWindowStatus();
      // Check status every minute
      const interval = setInterval(checkWindowStatus, 60000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [customerPhone]);

  const checkWindowStatus = async () => {
    if (!customerPhone) return;

    try {
      // Check if conversation window is open using tenant-specific function
      const { data: userData } = await supabase
        .from('users')
        .select('tenant_id, tenants!inner(schema_name)')
        .eq('auth_user_id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (!userData?.tenant_id || !userData.tenants?.schema_name) return;

      // Use tenant-specific function to check window status
      // Normalize phone number to match database format (without '+' prefix)
      const normalizedPhone = normalizePhoneNumber(customerPhone);
      const { data: windowOpen } = await supabase.rpc('is_tenant_conversation_window_open', {
        tenant_schema: userData.tenants.schema_name,
        phone_param: normalizedPhone
      });

      if (windowOpen) {
        // For now, we'll set a default 24-hour window expiration
        // In a real implementation, you'd get this from the tenant schema
        const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000);
        const now = new Date();
        const timeRemaining = Math.max(0, expiresAt.getTime() - now.getTime());
        
        setWindowStatus({
          isOpen: true,
          expiresAt: expiresAt.toISOString(),
          timeRemaining: formatTimeRemaining(timeRemaining)
        });
        onStatusChange?.(true);
      } else {
        setWindowStatus({
          isOpen: false
        });
        onStatusChange?.(false);
      }
    } catch (error) {
      console.error('Error checking window status:', error);
      setWindowStatus({ isOpen: false });
      onStatusChange?.(false);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeRemaining = (milliseconds: number): string => {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 1) {
      return `${hours}h ${minutes}m`;
    } else if (hours === 1) {
      return `1h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return '<1m';
    }
  };

  if (!customerPhone) {
    return null;
  }

  if (loading) {
    return (
      <Badge variant="secondary" className={className}>
        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
        Checking...
      </Badge>
    );
  }

  if (!windowStatus) {
    return (
      <Badge variant="destructive" className={className}>
        <AlertCircle className="h-3 w-3 mr-1" />
        Status unknown
      </Badge>
    );
  }

  return (
    <Badge 
      variant={windowStatus.isOpen ? "secondary" : "outline"} 
      className={className}
    >
      {windowStatus.isOpen ? (
        <>
          <CheckCircle className="h-3 w-3 mr-1" />
          Open
        </>
      ) : (
        <>
          <AlertCircle className="h-3 w-3 mr-1" />
          Closed
        </>
      )}
    </Badge>
  );
}